import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CalAngularService } from '@cvx/cal-angular';
import { NextPageService } from '@cvx/nextpage';
import { DataService } from './core/service/data_service';
import { UserDetails } from './core/interface/user';
import { SessionStorageService } from './core/service/session-storage';
import { environment } from '../environments/environment';
import { UserAccessService } from './core/service/user-access.service';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { application } from './core/constants/constants';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.less',
})
export class AppComponent implements OnInit, OnDestroy {
  loggedIn!: boolean;
  currentUser: UserDetails[] = [];
  userAccessSubscription!: Subscription;
  userInitiatedSignIn!: Subscription;
  getAADToken!: Subscription;
  validUser: boolean = false;
  invalidUser: boolean = false;
  showLoader: boolean = true;
  applicationConst = application;

  constructor(
    private NP: NextPageService,
    private authService: CalAngularService,
    private dataService: DataService,
    private session: SessionStorageService,
    private userAccess: UserAccessService,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router
  ) {
    this.NP.options.sitename = 'RFMViewer';
  }

  async ngOnInit() {
    this.loggedIn = false;
    try {
      this.logIn();
      this.getToken();
      await this.getUserImpersonationToken();
      this.getUserAccess();
    } catch (error) {
      console.error('Error during initialization:', error);
    }
    //on page reload
    this.router.navigate(['/']);
  }

  getUserAccess() {
    this.userAccessSubscription = this.userAccess.getUserAccess().subscribe({
      next: (response) => {
        this.showLoader = false;
        if (response.status === 423) {
          this.validUser = false;
          this.invalidUser = true;
        } else {
          this.validUser = true;
          this.invalidUser = false;
          this.session.setItem('userAccess', response.body);
        }
        this.changeDetectorRef.detectChanges();
      },
      error: (error) => {
        this.showLoader = false;
        console.error('Error Fetching data :', error);
      },
    });
  }

  // to login the user through CAL Service
  async logIn() {
    this.userInitiatedSignIn = this.authService
      .userInitiatedSignIn()
      .subscribe({
        next: () => {
          if (this.authService.isUserSignedIn()) {
            this.loggedIn = true;
            this.currentUserDetails();
          } else {
            this.loggedIn = false;
          }
        },
        error: (error) => {
          console.error('Error Fetching data :', error);
        },
      });
  }

  // to get logged-in user details
  async currentUserDetails(): Promise<UserDetails[]> {
    const result = await new Promise<boolean>((resolve) => {
      this.currentUser = [];
      this.authService
        .getUserPropertiesFromMsGraph([
          'givenName',
          'cai',
          'jobTitle',
          'surname',
          'mail',
          'Id',
        ])
        .subscribe((graphProp) => {
          if (graphProp) {
            this.currentUser.push({
              firstName: graphProp['givenName'],
              lastName: graphProp['surname'],
              emailId: graphProp['mail'],
              userId: graphProp['id'],
              fullName: `${graphProp['givenName']} ${graphProp['surname']}`,
            });
            this.session.setItem(
              'currentUser',
              JSON.parse(JSON.stringify(this.currentUser))
            );
            resolve(true);
          } else {
            resolve(false);
          }
        });
    });

    switch (result) {
      case true: {
        this.dataService.getUserDetail(this.currentUser);
        return this.currentUser;
      }
      case false: {
        return [];
      }
    }
  }

  // get TOKEN and logged-in user roles, and store in session-storage
  getToken() {
    this.getAADToken = this.authService.getAADToken().subscribe({
      next: (token: any) => {
        if (token) {
          this.session.setItem('token', JSON.parse(JSON.stringify(token)));
          if (this.authService.cvxClaimsPrincipal.roles) {
            this.session.setItem(
              'roles',
              JSON.parse(
                JSON.stringify(this.authService.cvxClaimsPrincipal.roles)
              )
            );
          }
        }
      },
      error: (error) => {
        console.error('Error Fetching data :', error);
      },
    });
  }

  // get user_impersonation TOKEN to make backend calls
  async getUserImpersonationToken() {
    return new Promise((resolve, reject) => {
      let envUrl = environment.production
        ? 'https://rfm.azure.chevron.com'
        : 'https://rfm-dev.azure.chevron.com';
      const scope = [`${envUrl}/user_impersonation`];
      this.getAADToken = this.authService.getAADToken(scope).subscribe({
        next: (token: any) => {
          if (token) {
            this.session.setItem(
              'backendToken',
              JSON.parse(JSON.stringify(token))
            );
          }
          resolve(true);
        },
        error: (error) => {
          console.error(
            'Error while fetching user_impersonation data :',
            error
          );
          reject();
        },
      });
    });
  }

  ngOnDestroy(): void {
    if (!!this.userAccessSubscription) {
      this.userAccessSubscription.unsubscribe();
    }

    if (this.userInitiatedSignIn) {
      this.userInitiatedSignIn.unsubscribe();
    }

    if (this.getAADToken) {
      this.getAADToken.unsubscribe();
    }
  }
}
